/**
 * B631 (b, read half) — the public REST wire for tenants who build their own
 * storefront. A CLOSED table maps resource URLs onto the buyer operations the
 * platform's own storefront already calls — a second WIRE over the same
 * registry, not a second backend (the OCTO wire's pattern, B526 (f1)). It is
 * data the authorizer (which op), the ops entry (how to translate), the stack
 * (which routes exist) and the OpenAPI generator (what to document) all read:
 * four readers, one table, no drift. Not an operation module: the registry
 * does not collect it.
 *
 * The caller is the storefront's own guest: `POST /v1/sessions` is the public
 * guest door (session.guest — tenant by header, rate-walled), and its token
 * is the Bearer every read carries, exactly as on `/ops`. Every route keeps
 * every /ops wall. Writes ride only onto buyer ops the kernel owner-fences
 * to the guest who made them (cart.create opens a new cart, so there is
 * nothing to fence), and a write's `Idempotency-Key` header is its clientKey
 * (Inv 4: a retry replays). The ticket lookup is the public code + email door
 * (order.tokens). Tenant API keys with scopes are leg (a), a separate
 * credential; nothing here mints or reads one.
 *
 * `path` names the path parameters, `query` the query parameters and `body`
 * the body fields the op takes; anything else the caller sends never reaches
 * the strict contract. The tenant never rides a parameter — the header and
 * the bearer carry it.
 */
export type RestWireTag = "Sessions" | "Products" | "Availability" | "Carts" | "Orders" | "Webhooks";

export interface RestWireRoute {
  readonly routeKey: string;
  readonly operation: string;
  readonly tag: RestWireTag;
  /** One line for the reference, in a storefront builder's words. */
  readonly summary: string;
  readonly path?: readonly string[];
  readonly query?: readonly string[];
  readonly body?: readonly string[];
}

export const REST_WIRE_ROUTES: readonly RestWireRoute[] = Object.freeze([
  {
    routeKey: "POST /v1/sessions",
    operation: "session.guest",
    tag: "Sessions",
    summary: "Start a guest session on one of your channels — the token is the Bearer for every other call",
    body: ["channelId"],
  },
  {
    routeKey: "GET /v1/products",
    operation: "catalog.list",
    tag: "Products",
    summary: "List the products a channel sells, with variants and display prices",
    query: ["channelId", "versionId"],
  },
  {
    routeKey: "GET /v1/products/{productId}",
    operation: "catalog.get",
    tag: "Products",
    summary: "Get one product as a channel sells it",
    path: ["productId"],
    query: ["channelId", "versionId"],
  },
  {
    routeKey: "GET /v1/products/{productId}/calendar",
    operation: "product.calendar",
    tag: "Availability",
    summary: "Get a product's \"from\" prices for a date range, per day or per showtime",
    path: ["productId"],
    query: ["channelId", "from", "to"],
  },
  {
    routeKey: "GET /v1/schedules/{scheduleId}/slots",
    operation: "slot.list",
    tag: "Availability",
    summary: "List the dated slots (showtimes, departures, entry times) a schedule runs",
    path: ["scheduleId"],
    query: ["serviceId", "from", "to"],
  },
  {
    routeKey: "GET /v1/slots/{slotId}",
    operation: "slot.get",
    tag: "Availability",
    summary: "Get one slot: when it starts and ends and whether it is on sale",
    path: ["slotId"],
  },
  {
    routeKey: "GET /v1/slots/{slotId}/availability",
    operation: "slot.availability",
    tag: "Availability",
    summary: "How many places are left in a slot, per capacity pool",
    path: ["slotId"],
  },
  {
    routeKey: "POST /v1/carts",
    operation: "cart.create",
    tag: "Carts",
    summary: "Open a cart on a channel — you choose the cart id, so a retried create is the same cart",
    body: ["cartId", "channelId", "currency"],
  },
  { routeKey: "GET /v1/carts/{cartId}", operation: "cart.get", tag: "Carts", summary: "Read a cart: its lines, what each costs and the totals", path: ["cartId"] },
  {
    routeKey: "POST /v1/carts/{cartId}/lines",
    operation: "cart.add",
    tag: "Carts",
    summary: "Add tickets to a cart — a product, a variant, a slot when the product is dated, and how many",
    path: ["cartId"],
    body: ["productId", "variantId", "slotId", "quantity"],
  },
  {
    routeKey: "PATCH /v1/carts/{cartId}/lines/{lineId}",
    operation: "cart.set_quantity",
    tag: "Carts",
    summary: "Change how many of a line the buyer wants",
    path: ["cartId", "lineId"],
    body: ["quantity"],
  },
  { routeKey: "DELETE /v1/carts/{cartId}/lines/{lineId}", operation: "cart.remove", tag: "Carts", summary: "Take a line out of a cart", path: ["cartId", "lineId"] },
  { routeKey: "POST /v1/carts/{cartId}/promotion", operation: "cart.apply_promo", tag: "Carts", summary: "Apply a promotion code to a cart", path: ["cartId"], body: ["code"] },
  {
    routeKey: "POST /v1/carts/{cartId}/checkout",
    operation: "cart.checkout",
    tag: "Carts",
    summary: "Turn the cart into an order awaiting payment — the places are held until holdExpiresAt",
    path: ["cartId"],
    body: ["buyerEmail", "buyerName", "buyerPhone", "acknowledged", "subscribe"],
  },
  {
    routeKey: "POST /v1/orders/{orderId}/payment",
    operation: "payment.intent.create",
    tag: "Orders",
    summary: "Start the card payment for an order — confirm the returned client secret with the provider's own SDK",
    path: ["orderId"],
    body: ["amount", "provider"],
  },
  { routeKey: "GET /v1/orders/{orderId}", operation: "order.get", tag: "Orders", summary: "Read an order the guest placed: its state, lines and money", path: ["orderId"] },
  {
    routeKey: "POST /v1/orders/{orderId}/resend",
    operation: "order.resend",
    tag: "Orders",
    summary: "Email an order's tickets again, to the buyer's own address on file — never another one",
    path: ["orderId"],
  },
  {
    routeKey: "POST /v1/tickets",
    operation: "order.tokens",
    tag: "Orders",
    summary: "Get an order's ticket tokens by its booking code and the buyer's email",
    body: ["code", "email"],
  },
  // B706 (b) — a key minted with webhooks:manage manages the endpoints IT created.
  {
    routeKey: "POST /v1/webhooks",
    operation: "webhook.create",
    tag: "Webhooks",
    summary: "Add an endpoint for order and ticket events — the signing secret is in this answer, once; store it",
    body: ["name", "url", "events"],
  },
  { routeKey: "GET /v1/webhooks", operation: "webhook.list", tag: "Webhooks", summary: "List the endpoints this key added" },
  { routeKey: "DELETE /v1/webhooks/{endpointId}", operation: "webhook.revoke", tag: "Webhooks", summary: "Stop sending events to an endpoint this key added", path: ["endpointId"] },
]);

const BY_KEY = new Map(REST_WIRE_ROUTES.map((r) => [r.routeKey, r]));

export function restWireRouteFor(routeKey: string): RestWireRoute | undefined {
  return BY_KEY.get(routeKey);
}

/**
 * The ONE route the stack carries for this wire (B631, measured 2026-10-03:
 * the dev stack sat at 492 of CloudFormation's 500 resources, and a route per
 * table entry took it to 502). API Gateway hands every /v1 call over under
 * this key with the concrete method and path; the authorizer and the ops
 * entry both resolve them through {@link restWireMatch} — the same table,
 * the same function, so the op one decides is the op the other runs.
 */
export const REST_WIRE_PROXY_ROUTE_KEY = "ANY /v1/{proxy+}";

const SEGMENT = /^\{([A-Za-z]+)\}$/;

/**
 * A concrete method + path → its table route and decoded path parameters, or
 * nothing. Closed: the method must be the route's, every literal segment must
 * be equal, every parameter segment non-empty and decodable, and the segment
 * counts equal — an unnamed path never reaches an operation.
 */
export function restWireMatch(
  method: string,
  rawPath: string,
): { readonly route: RestWireRoute; readonly pathParameters: Readonly<Record<string, string>> } | undefined {
  const verb = method.toUpperCase();
  const segments = rawPath.split("/");
  for (const route of REST_WIRE_ROUTES) {
    const [routeVerb, template] = route.routeKey.split(" ") as [string, string];
    if (routeVerb !== verb) continue;
    const parts = template.split("/");
    if (parts.length !== segments.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < parts.length && ok; i += 1) {
      const name = SEGMENT.exec(parts[i]!)?.[1];
      if (name === undefined) {
        ok = parts[i] === segments[i];
        continue;
      }
      const raw = segments[i]!;
      if (raw === "") {
        ok = false;
        continue;
      }
      try {
        params[name] = decodeURIComponent(raw);
      } catch {
        ok = false;
      }
    }
    if (ok) return { route, pathParameters: params };
  }
  return undefined;
}
